import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ChangedDot from './ChangedDot';
import { SheetContext, SheetContextValue, SheetEntry, sectionDomId } from './context';
import './sheet.css';

/**
 * Σελίδα ρυθμίσεων «Φύλλο» (σχέδιο A, έγκριση 13/10/2026): κεφαλίδα (τίτλος + μία γραμμή), ευρετήριο
 * «Σε αυτή τη σελίδα» αριστερά — ακολουθεί την κύλιση, τελεία σε ενότητα με αλλαγές — και οι
 * ενότητες (SheetSection) η μία κάτω από την άλλη, που δηλώνονται μόνες τους μέσω context.
 * Η μπάρα «Έχεις αλλαγές…» μπαίνει ως τελευταίο παιδί: μετρά το πλάτος του φύλλου, όχι της σελίδας.
 */

export type SheetLayout = 'wide' | 'medium' | 'narrow';

// Πλάτος του χώρου της σελίδας (clientWidth, όπως ο Κατάλογος). 0 = άγνωστο (πριν από τη μέτρηση, jsdom).
const INDEX_MIN = 960;
const COLUMNS_MIN = 640;

export const sheetLayoutFor = (width: number): SheetLayout => {
  if (!(width > 0) || width >= INDEX_MIN) return 'wide';
  return width >= COLUMNS_MIN ? 'medium' : 'narrow';
};

// «Τρέχουσα» ενότητα = η τελευταία που περνά από το πάνω 32% της οθόνης (όπως στο δείγμα).
const BAND = '0px 0px -68% 0px';
// Μετά από κλικ στο ευρετήριο η ομαλή κύλιση περνά από άλλες ενότητες· δεν αλλάζουν την επιλογή.
const CLICK_LOCK_MS = 900;

export interface SettingsSheetProps {
  title: string;
  subtitle?: React.ReactNode;
  /** Μικρό κεφαλαίο πάνω από τον τίτλο: η ομάδα του μενού («Κατάστημα», «Ταμείο»). */
  eyebrow?: string;
  /** Ευρετήριο αριστερά, όταν χωρά. false για σελίδα με μία ενότητα. */
  index?: boolean;
  children: React.ReactNode;
}

const byDomOrder = (a: SheetEntry, b: SheetEntry): number => {
  if (!a.el || !b.el || a.el === b.el) return 0;
  // eslint-disable-next-line no-bitwise
  return a.el.compareDocumentPosition(b.el) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
};

const reducedMotion = (): boolean => {
  try {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
  } catch {
    return false;
  }
};

export const SettingsSheet: React.FC<SettingsSheetProps> = ({ title, subtitle, eyebrow, index = true, children }) => {
  const { t } = useTranslation(['settings']);
  const capId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const [layout, setLayout] = useState<SheetLayout>('wide');
  const [entries, setEntries] = useState<SheetEntry[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const entriesRef = useRef(entries);
  entriesRef.current = entries;
  const lockUntil = useRef(0);

  // Από το πλάτος του χώρου, όχι του παραθύρου: σε οθόνες ≥768px η εφαρμογή κάνει zoom (public/index.html).
  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;
    const measure = () => setLayout(sheetLayoutFor(el.clientWidth));
    measure();
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  const register = useCallback((entry: SheetEntry) => {
    setEntries(prev => {
      const i = prev.findIndex(e => e.id === entry.id);
      if (i < 0) return [...prev, entry].sort(byDomOrder);
      const old = prev[i];
      if (old.label === entry.label && old.dirty === entry.dirty && old.el === entry.el) return prev;
      const next = prev.slice();
      next[i] = entry;
      return old.el === entry.el ? next : next.sort(byDomOrder);
    });
  }, []);
  const unregister = useCallback((id: string) => {
    setEntries(prev => (prev.some(e => e.id === id) ? prev.filter(e => e.id !== id) : prev));
  }, []);
  const ctx = useMemo<SheetContextValue>(() => ({ register, unregister }), [register, unregister]);

  // Ποια ενότητα διαβάζεις. Χωρίς IntersectionObserver (jsdom, πολύ παλιός browser) μένει η πρώτη ή
  // όποια πατήθηκε στο ευρετήριο.
  useEffect(() => {
    if (typeof IntersectionObserver !== 'function') return undefined;
    const inBand = new Set<string>();
    let atEnd = false;
    const pick = () => {
      if (Date.now() < lockUntil.current) return;
      const list = entriesRef.current;
      if (list.length === 0) return;
      // Στο τέλος της σελίδας μια κοντή τελευταία ενότητα δεν φτάνει ποτέ στη ζώνη.
      if (atEnd && window.scrollY > 0) {
        setActiveId(list[list.length - 1].id);
        return;
      }
      const hit = list.filter(e => inBand.has(e.id));
      if (hit.length > 0) setActiveId(hit[hit.length - 1].id);
    };
    const band = new IntersectionObserver(items => {
      items.forEach(item => {
        const id = (item.target as HTMLElement).dataset.sheetSection;
        if (!id) return;
        if (item.isIntersecting) inBand.add(id);
        else inBand.delete(id);
      });
      pick();
    }, { rootMargin: BAND, threshold: 0 });
    entries.forEach(e => { if (e.el) band.observe(e.el); });
    const end = new IntersectionObserver(items => {
      atEnd = items.some(item => item.isIntersecting);
      pick();
    });
    if (endRef.current) end.observe(endRef.current);
    return () => {
      band.disconnect();
      end.disconnect();
    };
  }, [entries]);

  const current = entries.some(e => e.id === activeId) ? activeId : (entries[0]?.id ?? null);

  const go = (event: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    event.preventDefault();
    lockUntil.current = Date.now() + CLICK_LOCK_MS;
    setActiveId(id);
    const el = entriesRef.current.find(e => e.id === id)?.el;
    if (!el) return;
    if (typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
    }
    // Το πληκτρολόγιο συνεχίζει από την ενότητα, όχι από το ευρετήριο.
    el.querySelector<HTMLElement>('[data-sheet-heading]')?.focus({ preventScroll: true });
  };

  const showIndex = index && layout === 'wide';

  return (
    <SheetContext.Provider value={ctx}>
      <div
        ref={rootRef}
        className="xs-wrap group/sheet"
        data-layout={layout}
        data-index={showIndex ? 'on' : 'off'}
        data-testid="settings-sheet"
      >
        {showIndex && (entries.length > 0 ? (
          <nav className="xs-index" aria-labelledby={capId}>
            <p id={capId} className="xs-index-cap">{t('settings:sheet.onThisPage')}</p>
            <ol>
              {entries.map(entry => (
                <li key={entry.id}>
                  <a
                    href={`#${sectionDomId(entry.id)}`}
                    aria-current={entry.id === current ? 'true' : undefined}
                    onClick={event => go(event, entry.id)}
                  >
                    {entry.label}
                    {entry.dirty && (
                      <>
                        <ChangedDot />
                        <span className="sr-only"> · {t('settings:sheet.unsavedSection')}</span>
                      </>
                    )}
                  </a>
                </li>
              ))}
            </ol>
          </nav>
        ) : (
          // Η στήλη κρατιέται και πριν δηλωθούν ενότητες (φόρτωση): το φύλλο δεν «πηδά» μετά.
          <div className="xs-index" aria-hidden="true" />
        ))}

        <article className="xs-sheet">
          <header className="xs-head">
            {eyebrow && <p className="xs-eyebrow">{eyebrow}</p>}
            <h1 className="xs-title">{title}</h1>
            {subtitle && <p className="xs-subtitle">{subtitle}</p>}
          </header>
          {children}
          <div ref={endRef} aria-hidden="true" />
        </article>
      </div>
    </SheetContext.Provider>
  );
};

export default SettingsSheet;
